import React, { useMemo, useState } from "react"
import moment from "moment"
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap"
import reel from "../../../assets/images/services-icon/reel.png"
import { BmdSkillbuilderPreviewModal } from "../../bmd/components/BmdSkillbuilderPreviewModal"

const COMPACT_CARD_ACTION_WIDTH = 136

// Shape attached by SkillbuilderActivityAttachment on the backend for
// activity_type=14 rows.
export type SkillbuilderAttached = {
  id: number
  skill_id?: number | null
  topic_id?: number | null
  skill_name?: string
  worksheet_type?: string
  misconception_type?: string | null
}

export type SkillbuilderActivityLike = {
  pk_lesson_activity_key: string
  fk_tut_key?: string
  fk_activity_node_id?: number
  activity_type: number
  status?: number
  roll_date?: string
  tutor_comments?: string | null
  marked_for_homework?: number
  skillbuilder?: SkillbuilderAttached | null
  skillbuilder_skill_name?: string
}

export type SkillbuilderCardCheckbox = {
  checkable?: boolean
  checked?: boolean
  onCheckedChanged?: () => unknown
}

export type SkillbuilderActivityCardProps = {
  activity: SkillbuilderActivityLike
  index: number
  isHomework?: boolean
  isPlanningSession?: boolean
  checkbox?: SkillbuilderCardCheckbox
  showContextMenu?: boolean
  /**
   * Override the built-in preview. There is only one way to preview a Skill
   * Builder, so the card opens its own modal by default and every caller gets
   * preview without wiring anything.
   */
  onPreview?: () => unknown
  onDelete?: () => unknown
  onRollOver?: () => unknown
  onCopyToHomework?: () => unknown
  onCopyToLessonPlan?: () => unknown
  onMoveToHomework?: () => unknown
  onMoveToLessonPlan?: () => unknown
  deleteEnabled?: boolean
  rollOverEnabled?: boolean
}

type ActionTone = "preview" | "delete" | "secondary" | "menu"

function getActionButtonStyle(tone: ActionTone): React.CSSProperties {
  const baseStyle: React.CSSProperties = {
    color: "#1b1b1b",
    border: "none",
    minHeight: "24px",
    padding: "4px 6px",
    lineHeight: 1.05,
    borderRadius: "4px",
  }
  if (tone === "preview") return { ...baseStyle, backgroundColor: "#c9d7f2" }
  if (tone === "delete") return { ...baseStyle, backgroundColor: "#ebc9aa" }
  return { ...baseStyle, backgroundColor: "#d8deec" }
}

const CompactActionButton = ({
  label,
  onClick,
  tone,
}: {
  label: React.ReactNode
  onClick: () => unknown
  tone: ActionTone
}) => (
  <button
    type="button"
    className="btn btn-sm w-100 fw-700"
    style={{ ...getActionButtonStyle(tone), fontSize: "9px" }}
    onMouseDown={(event) => event.stopPropagation()}
    onClick={(event) => {
      event.stopPropagation()
      void onClick()
    }}
  >
    {label}
  </button>
)

const EmptyActionCell = () => <div style={{ minHeight: "24px" }} />

/**
 * A Skill Builder has no flow_status of its own: it is submitted or it is not.
 * The pass/fail verdict deliberately does NOT appear here. The tutor reads that
 * off the diagnostics report, where the pass mark is shown beside it.
 */
function getSkillbuilderStatusMeta(activityStatus: number | undefined): {
  label: string
  className: string
} {
  if (activityStatus === 1) {
    return { label: "Completed", className: "text-success fw-700" }
  }
  return { label: "Not Started", className: "cs-txt-danger fw-700" }
}

/**
 * What the student got wrong, in the tutor's words. The raw enum is never
 * shown: RECENTLY_LEARNED / LACK_OF_DEPTH mean nothing on a lesson plan.
 */
function getMisconceptionLabel(
  value: string | null | undefined,
): string | null {
  if (value === "RECENTLY_LEARNED") return "Recently learned"
  if (value === "LACK_OF_DEPTH") return "Lack of depth"
  if (value === "FUNDAMENTAL") return "Fundamental"
  return null
}

function getRolledOverText(rollDate: unknown): string | null {
  if (!rollDate || typeof rollDate !== "string") return null
  return `Rolled over from ${moment(rollDate, moment.ISO_8601).format(
    "DD/MMM/YYYY",
  )}`
}

export const SkillbuilderActivityCard: React.FC<
  SkillbuilderActivityCardProps
> = ({
  activity,
  index,
  isHomework = false,
  isPlanningSession = false,
  checkbox,
  showContextMenu = true,
  onPreview,
  onDelete,
  onRollOver,
  onCopyToHomework,
  onCopyToLessonPlan,
  onMoveToHomework,
  onMoveToLessonPlan,
  deleteEnabled = false,
  rollOverEnabled = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)

  const skillbuilder = activity.skillbuilder || null
  // The flat field is the student payload's spelling; the object is the
  // tutor's. Either may be the one that arrived, so both are consulted.
  const skillName =
    skillbuilder?.skill_name || activity.skillbuilder_skill_name || ""
  const worksheetType = skillbuilder?.worksheet_type || ""
  const misconception = getMisconceptionLabel(skillbuilder?.misconception_type)

  // fk_activity_node_id IS the skill builder id for activity_type 14, so the
  // preview works even if the backend attachment is missing.
  const skillbuilderId = skillbuilder?.id ?? activity.fk_activity_node_id ?? null
  const previewAction =
    onPreview || (skillbuilderId ? () => setPreviewOpen(true) : null)

  const statusMeta = getSkillbuilderStatusMeta(activity.status)
  const rolledOverText = getRolledOverText(activity.roll_date)
  const titleText = skillName || "Skill Builder"
  const markedForHomework = Number(activity.marked_for_homework) === 1

  // Labels match getPlanningMoveAction / getPlanningCopyAction in
  // PlanLessonCards.tsx. If those change, change these with them.
  const moveAction = useMemo(() => {
    if (!isPlanningSession) return null
    if (!markedForHomework && onMoveToHomework) {
      return { label: "Move to h/w", onClick: onMoveToHomework }
    }
    if (markedForHomework && onMoveToLessonPlan) {
      return { label: "Move to lesson", onClick: onMoveToLessonPlan }
    }
    return null
  }, [
    isPlanningSession,
    markedForHomework,
    onMoveToHomework,
    onMoveToLessonPlan,
  ])

  const copyAction = useMemo(() => {
    if (isPlanningSession) {
      if (!markedForHomework && onCopyToHomework) {
        return { label: "Copy to h/w", onClick: onCopyToHomework }
      }
      if (markedForHomework && onCopyToLessonPlan) {
        return { label: "Copy to lesson", onClick: onCopyToLessonPlan }
      }
      return null
    }
    if (!isHomework && !markedForHomework && onCopyToHomework) {
      return { label: "Copy to h/w", onClick: onCopyToHomework }
    }
    return null
  }, [
    isPlanningSession,
    isHomework,
    markedForHomework,
    onCopyToHomework,
    onCopyToLessonPlan,
  ])

  const menuItems = useMemo(() => {
    const items: Array<{ key: string; label: string; onClick: () => unknown }> =
      []
    if (rollOverEnabled && onRollOver) {
      items.push({
        key: "rollOver",
        label: "Roll over to next session",
        onClick: onRollOver,
      })
    }
    return items
  }, [rollOverEnabled, onRollOver])

  return (
    <>
    <div
      className="set1 bg-supermild"
      data-key={activity.pk_lesson_activity_key}
      style={{ minHeight: "unset", padding: "4px 6px" }}
    >
      <div className="d-flex justify-content-between align-items-start gap-2">
        {/* Left column: content */}
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          {rolledOverText ? (
            <span
              className="cs-txt-danger me-2"
              style={{ fontSize: "8px", display: "block", lineHeight: 1.1 }}
            >
              {rolledOverText}
            </span>
          ) : null}

          <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
            <span
              className={statusMeta.className}
              style={{ fontSize: "8px", lineHeight: 1 }}
            >
              {statusMeta.label}
            </span>

            {misconception ? (
              <span
                className="text-dark"
                style={{ fontSize: "8px", lineHeight: 1 }}
                title={`Assigned because the student's error looked like: ${misconception}`}
              >
                {misconception}
              </span>
            ) : null}

            {activity?.tutor_comments ? (
              <span
                className="text-dark"
                style={{ cursor: "pointer", fontSize: "9px", lineHeight: 1 }}
                title={`Comment: ${activity.tutor_comments}`}
              >
                <i className="fas fa-info-circle"></i>
              </span>
            ) : null}

            {checkbox?.checkable ? (
              <input
                type="checkbox"
                checked={!!checkbox.checked}
                onMouseDown={(event) => event.stopPropagation()}
                onChange={(event) => {
                  event.stopPropagation()
                  void checkbox.onCheckedChanged?.()
                }}
              />
            ) : null}
          </div>

          <h6
            className="text-dark fw-700"
            style={{
              fontSize: "10px",
              lineHeight: 1.15,
              marginBottom: "3px",
              overflow: "hidden",
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
            }}
            title={titleText}
          >
            {index + 1}) {titleText}
          </h6>

          <div
            className="border-top"
            style={{ marginTop: "2px", marginBottom: "3px", opacity: 0.7 }}
          ></div>

          <div className="d-flex justify-content-between align-items-center mb-1 gap-2">
            <div
              className="d-flex align-items-center fw-400 text-dark"
              style={{ fontSize: "9px", lineHeight: 1.05 }}
            >
              <span>
                <img className="me-05" width={12} src={reel} alt="" />
              </span>
              Skill Builder
            </div>
            {worksheetType ? (
              <span
                className="text-dark text-truncate"
                style={{ fontSize: "8px", lineHeight: 1.05, maxWidth: "55%" }}
                title={worksheetType}
              >
                {worksheetType}
              </span>
            ) : null}
          </div>

          <div className="question-count mt-0">
            <div style={{ fontSize: "9px" }}>&nbsp;</div>
          </div>
        </div>

        {/* Right column: action buttons + menu */}
        {showContextMenu ? (
          <div
            style={{
              width: `${COMPACT_CARD_ACTION_WIDTH}px`,
              flex: `0 0 ${COMPACT_CARD_ACTION_WIDTH}px`,
            }}
          >
            <div className="d-grid" style={{ gap: "3px" }}>
              {previewAction ? (
                <CompactActionButton
                  label="Preview"
                  onClick={previewAction}
                  tone="preview"
                />
              ) : (
                <EmptyActionCell />
              )}

              <div
                className="d-grid"
                style={{ gridTemplateColumns: "1fr 1fr", gap: "3px" }}
              >
                <div className="d-grid" style={{ gap: "3px" }}>
                  {deleteEnabled && onDelete ? (
                    <CompactActionButton
                      label="Delete"
                      onClick={onDelete}
                      tone="delete"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}

                  {menuItems.length > 0 ? (
                    <Dropdown
                      isOpen={menuOpen}
                      toggle={() => setMenuOpen((prev) => !prev)}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <DropdownToggle
                        caret={false}
                        className="btn btn-sm w-100 fw-700"
                        style={{
                          ...getActionButtonStyle("menu"),
                          fontSize: "9px",
                        }}
                      >
                        <i className="mdi mdi-dots-horizontal"></i>
                      </DropdownToggle>
                      <DropdownMenu className="custom-position">
                        {menuItems.map((item) => (
                          <DropdownItem
                            key={item.key}
                            onClick={(event) => {
                              event.stopPropagation()
                              void item.onClick()
                            }}
                          >
                            {item.label}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  ) : (
                    <EmptyActionCell />
                  )}
                </div>

                <div className="d-grid" style={{ gap: "3px" }}>
                  {moveAction ? (
                    <CompactActionButton
                      label={moveAction.label}
                      onClick={moveAction.onClick}
                      tone="secondary"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}

                  {copyAction ? (
                    <CompactActionButton
                      label={copyAction.label}
                      onClick={copyAction.onClick}
                      tone="secondary"
                    />
                  ) : (
                    <EmptyActionCell />
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>

    {previewOpen && skillbuilderId ? (
      <BmdSkillbuilderPreviewModal
        skillbuilderId={skillbuilderId}
        skillName={skillName}
        // Scoped to the ACTIVITY, so the preview shows what this student
        // answered rather than the answer key.
        activityKey={activity.pk_lesson_activity_key}
        onClose={() => setPreviewOpen(false)}
      />
    ) : null}
    </>
  )
}

export default SkillbuilderActivityCard
